<template>
<div>
  <v-toolbar
    id="core-toolbar"
    flat
    color="tertiary"
  >
    <div class="v-toolbar-title" >
      <v-toolbar-title class="tertiary--text font-weight-light">
        <v-btn
          class="default v-btn--simple"
          data-testid="toggle-app-drawer"
          icon
          @click.stop="toggleDrawer"
        >
          <v-icon color="tertiary">mdi-view-list</v-icon>
        </v-btn>
      </v-toolbar-title>
    </div>
    <v-spacer />
    <v-toolbar-items>
      <v-flex
        align-center
        layout
        py-2
      >
        <Notifications
          :color="notificationsColor"
          @contract-notification="handleContractNotification"
          @insurance-notification="handleInsuranceNotification"
          @account-certification-notification="handleAccountCertificationNotification"
          @device-notification="handleDeviceNotification"
          @pending-approval-notification="handlePendingApprovalNotification"
          @returned-approval-notification="handleReturnedApprovalNotification"
          @account-contact-email-invalid="handleInvalidAccountEmail"
          @connection-string-error-notification="handleConnectionStringErrorNotification"
          @src-failure-notification="handleSrcFailureNotification"
          @due-diligence-pending-notification="jumpToDueDiligenceApprovals"
          @due-diligence-returned-notification="jumpToTractDueDiligence"
          @due-diligence-approved-notification="jumpToTractDueDiligence"
        />
        <v-btn class="toolbar-items" color="grey" icon @click="jumpToSettings">
          <v-icon>mdi-cog</v-icon>
        </v-btn>

        <v-menu offset-y rounded right :close-on-content-click="false">
          <template v-slot:activator="{ on }">
            <v-btn icon color="grey" class="toolbar-items mr-2" v-on="on"><v-icon>mdi-account-circle</v-icon></v-btn>
          </template>
          <v-card style="max-width: 350px;">
            <v-card-title class="pb-0 pt-2">
              <v-row justify="center">
                <v-col cols="auto">
                  <span>
                    {{ username }}
                  </span>
                </v-col>
              </v-row>
            </v-card-title>
            <v-card-text>
              <v-row justify="center">
                <v-col cols="auto">
                  <span>{{ this.userInfo.preferredUsername }}</span>
                </v-col>
                <v-col cols="12" v-if="subtenants && subtenants.length > 0">
                  <v-select
                    class="mt-n2 mb-n4"
                    outlined
                    dense
                    :items="subtenants"
                    item-text="name"
                    return-object
                    @change="subtenantChosen"
                    :menu-props="{ offsetY: true }"
                    :placeholder="$t('switchBusiness')">
                  </v-select>
                </v-col>
                <v-col cols="12">
                  <v-btn
                    class="mt-n4"
                    block
                    color="primary"
                    elevation="0"
                    @click="onSignOutPressed()"
                  >
                    <v-row dense justify="center">
                      <v-icon class="mr-2">mdi-logout</v-icon>
                      <span> {{$t('signOut')}} </span>
                    </v-row>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-menu>

        <v-col class="mt-6 text-right" style="color: #00000099">
          <v-row>
            <p>
              <v-tooltip bottom color="error" v-if="inactive">
                <template #activator="{on}">
                  <v-icon small v-on="on" color="error">
                    mdi-close-circle
                  </v-icon>
                </template>
                <span class="white--text subtitle-1">{{$t('youAreInactive')}}</span>
              </v-tooltip>
                {{username}}
                <br/>
                {{companyName}}
              </p>
          </v-row>
          <v-row>
            <p></p>
          </v-row>
        </v-col>

      </v-flex>
    </v-toolbar-items>
  </v-toolbar>
  <v-dialog v-model="contractExpirationDateDialog" width="500px">
    <UpdateExpirationDate
    :contractId="focusedContractId"
    @cancel="close"
    @contract-updated="close(true)"
    @jump="close(false)"
    v-if="contractExpirationDateDialog && focusedContractId"
    />
  </v-dialog>
  <v-dialog v-model="insuranceExpiryDateDialog" width="500px">
    <UpdateAccountInsurancePolicies
    v-if="insuranceExpiryDateDialog && focusedAccountId"
    @close="close"
    :accountId="focusedAccountId"
    />
  </v-dialog>
  <v-dialog v-model="deviceExpirationDateDialog" width="500px">
    <UpdateDeviceRegistration
    :registrationId="focusedDeviceId"
    @cancel="close"
    @registration-updated="close(true)"
    v-if="deviceExpirationDateDialog && focusedDeviceId"
    />
  </v-dialog>
  <v-dialog v-model="certificationExpirationDialog" width="500px">
    <UpdateAccountCertification
      v-if="certificationExpirationDialog && focusedCertification"
      :accountId="focusedCertification.AccountId"
      :accountCertificationId="focusedCertification.AccountCertificationId"
      :accountName="focusedCertification.AccountName"
      @account-certification-changed="close(true)"
      @close="close"
    />
  </v-dialog>
</div>
</template>

<script>
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex'
import RouterJump from '@/model/RouterJump'
import { SasTokenLocations } from '@/utils/LocalStorageActor'
import { mobileCompatible } from '@/utils/DeviceUtility'
import { UserStatus } from '@/utils/Enumerations.js'

export default {
  name: 'Toolbar',

  props: {
    subtenants: {
      type: Array,
      required: false,
      default: undefined
    }
  },

  data: () => ({
    responsive: false,
    contractExpirationDateDialog: false,
    insuranceExpiryDateDialog: false,
    certificationExpirationDialog: false,
    deviceExpirationDateDialog: false,
    focusedContractId: undefined,
    focusedAccountId: undefined,
    focusedCertification: undefined,
    focusedDeviceId: undefined
  }),

  components: {
    UpdateExpirationDate: () => import('@/components/contract/UpdateExpirationDate.vue'),
    UpdateDeviceRegistration: () => import('@/components/core/UpdateDeviceRegistration.vue'),
    Notifications: () => import('@/components/core/Notifications.vue'),
    UpdateAccountInsurancePolicies: () => import('@/components/account/Insurance/UpdateAccountInsurancePolicies.vue'),
    UpdateAccountCertification: () => import('@/components/account/certifications/UpdateAccountCertification.vue')
  },

  computed: {
    ...mapGetters('user', ['companyInfo', 'userInfo']),
    ...mapGetters('notifications', ['userNotifications']),
    ...mapGetters('contract', ['filter']),
    ...mapState('app', ['mini']),

    username () {
      if (this.userInfo !== undefined && this.companyInfo !== undefined) {
        return `${this.userInfo.name}`
      }

      return ''
    },

    companyName () {
      if (this.companyInfo !== undefined) {
        return `${this.companyInfo.name}`
      }

      return ''
    },

    inactive () {
      if (this.userInfo !== undefined && this.companyInfo !== undefined) {
        return this.userInfo.status === UserStatus.Inactive.value
      }

      return false
    },

    notificationsColor () {
      const count = this.userNotifications.length
      if (count > 0) return 'primary'
      return 'grey'
    }
  },

  mounted () {
    this.onResponsiveInverted()
    window.addEventListener('resize', this.onResponsiveInverted)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResponsiveInverted)
  },

  methods: {
    ...mapActions('contract', ['fetchContracts']),
    ...mapActions('account', ['fetchAccounts']),
    ...mapActions('device-registration', ['fetchDeviceRegistrations']),
    ...mapActions('notifications', ['fetchNotifications']),
    ...mapActions('user', ['logout']),
    ...mapMutations('app', ['setMini']),
    ...mapMutations('global', ['setRouterJump', 'setIsLoggingOut']),

    toggleDrawer () {
      if (!this.responsive) {
        this.setMini(!this.mini)
      } else {
        this.$emit('toggleDrawer')
      }
    },

    onResponsiveInverted () {
      this.responsive = mobileCompatible()
    },

    handleContractNotification (contractId) {
      this.focusedContractId = contractId
      this.contractExpirationDateDialog = true
    },

    handleInsuranceNotification (accountId) {
      this.focusedAccountId = accountId
      this.insuranceExpiryDateDialog = true
    },

    handleAccountCertificationNotification (certification) {
      this.focusedCertification = certification
      this.certificationExpirationDialog = true
    },

    handleDeviceNotification (deviceId) {
      this.focusedDeviceId = deviceId
      this.deviceExpirationDateDialog = true
    },

    handlePendingApprovalNotification (contractId) {
      this.jumpToContractDetails(contractId)
    },

    handleReturnedApprovalNotification (contractId) {
      this.jumpToContractDetails(contractId)
    },

    handleInvalidAccountEmail (details) {
      this.jumpToAccountDetails(details.AccountId)
    },

    handleConnectionStringErrorNotification () {
      this.jumpToCompanyInfo()
    },

    handleSrcFailureNotification (contractId) {
      this.jumpToContractDetails(contractId)
    },

    close (refresh) {
      if (this.focusedContractId && refresh && this.$route.path === '/contracts') {
        this.fetchContracts(this.filter)
      }

      if (this.focusedAccountId && refresh && this.$route.path === '/accounts') {
        this.fetchAccounts()
      }

      if (this.focusedCertification && refresh && this.$route.path === '/accounts') {
        this.fetchAccounts()
      }

      if (this.focusedDeviceId && refresh && this.$route.path === '/deviceRegistrations') {
        this.fetchDeviceRegistrations()
      }

      this.focusedContractId = undefined
      this.focusedAccountId = undefined
      this.focusedCertification = undefined
      this.focusedDeviceId = undefined
      this.contractExpirationDateDialog = false
      this.insuranceExpiryDateDialog = false
      this.certificationExpirationDialog = false
      this.deviceExpirationDateDialog = false

      !refresh || this.fetchNotifications()
    },

    jumpToContractDetails (contractId) {
      this.setRouterJump(new RouterJump('Toolbar', 'Contracts', {
        contractId: contractId
      }))
      if (this.$router.currentRoute.path !== '/contracts') this.$router.push('contracts')
      this.close(false)
    },

    jumpToDueDiligenceApprovals () {
      if (this.$router.currentRoute.path !== '/tract-due-diligence') this.$router.push('tract-due-diligence')
      this.close(false)
    },

    jumpToTractDueDiligence (tractId) {
      this.setRouterJump(new RouterJump('Toolbar', 'Tracts', {
        tractId: tractId
      }, { initialTab: 'dueDiligence' }))
      if (this.$router.currentRoute.path !== '/tracts') this.$router.push('tracts')
      this.close(false)
    },

    jumpToSettings () {
      if (this.$router.currentRoute.path !== '/settings') this.$router.push('settings')
    },

    jumpToAccountDetails (accountId) {
      this.setRouterJump(new RouterJump('Toolbar', 'Accounts', {
        accountId: accountId
      }))
      if (this.$router.currentRoute.path !== '/accounts') this.$router.push('accounts')
      this.close(false)
    },

    jumpToCompanyInfo () {
      this.setRouterJump(new RouterJump('Toolbar', 'Company Information'))
      if (this.$router.currentRoute.path !== '/settings') this.$router.push('settings')
      this.close(false)
    },

    deleteTokensFromLocalStorage () {
      // SasTokenLocations values are getters returning the tenant-prefixed storage key ("CODE:…_SAS");
      // removing the bare label names left every cached SAS behind on sign-out.
      for (const label in SasTokenLocations) {
        localStorage.removeItem(SasTokenLocations[label])
      }
    },

    onSignOutPressed () {
      this.deleteTokensFromLocalStorage()
      this.setIsLoggingOut(true)
      this.logout(true)
    },

    subtenantChosen (subtenant) {
      this.$emit('subtenant-chosen', subtenant)
    }
  }
}
</script>

<style>
#core-toolbar a {
  text-decoration: none;
}

.v-toolbar__content {
  margin: 0px !important;
}
</style>
